import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, Camera, Mail, MapPin, Phone, Save, User, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BirthDateFields, { validBirthDate } from "./BirthDateFields";

export default function ProfileDialog({ open, onClose }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [usState, setUsState] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const initializedFor = useRef(null);

  useEffect(() => {
    if (!open || !user || user === false) {
      initializedFor.current = null;
      return;
    }
    const identity = user.id || user.email;
    if (initializedFor.current === identity) return;
    initializedFor.current = identity;
    setSaveError("");
    setUsername(user.username || "");
    setFullName(user.full_name || "");
    setEmail(user.email || "");
    setDateOfBirth(user.date_of_birth || "");
    setUsState(user.state || "");
    setProfileImage(user.profile_image_url || "");
  }, [open, user]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousOverflowX = document.body.style.overflowX;
    document.body.style.overflow = "hidden";
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overflowX = previousOverflowX;
    };
  }, [open]);

  if (!open || !user || user === false) return null;

  const initial = (username || email || "S").charAt(0).toUpperCase();
  const displayPhone = formatProfilePhone(user.phone);

  const pickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setImageBusy(true);
    try {
      const resized = await resizeImage(file);
      setProfileImage(resized);
      toast.success("Profile picture ready");
    } catch {
      toast.error("Could not load that image");
    } finally {
      setImageBusy(false);
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaveError("");
    if (!user.date_of_birth && dateOfBirth && !validBirthDate(dateOfBirth)) {
      setSaveError("Choose a valid month, day, and year for your date of birth.");
      return;
    }
    setSaving(true);
    try {
      const changes = {};
      if (username !== (user.username || "")) changes.username = username;
      if (fullName !== (user.full_name || "")) changes.full_name = fullName;
      if (!user.date_of_birth && dateOfBirth) changes.date_of_birth = dateOfBirth;
      if (usState !== (user.state || "")) changes.state = usState;
      if (profileImage !== (user.profile_image_url || "")) changes.profile_image_url = profileImage;
      const { data } = await api.patch("/auth/profile", changes);
      setUser(data);
      toast.success("Profile updated");
      onClose();
    } catch (e) {
      const message = formatApiError(e);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const dialog = (
    <div
      className="fixed inset-0 z-[200] flex touch-pan-y items-stretch justify-center overflow-x-hidden overflow-y-auto overscroll-contain bg-black/80 px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-[max(0.5rem,env(safe-area-inset-top))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={saveProfile}
        onClick={(e) => e.stopPropagation()}
        className="flex h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] min-w-0 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#07110B] shadow-2xl sm:my-auto sm:h-auto sm:max-h-[92vh] sm:max-w-lg"
      >
        <div className="flex min-w-0 shrink-0 items-start justify-between gap-4 px-4 pt-4 sm:px-6 sm:pt-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Profile</p>
            <h2 className="mt-1 font-display text-2xl font-black text-white">My account</h2>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overscroll-contain overflow-x-hidden overflow-y-auto px-4 pb-4 pt-4 sm:px-6">
          <div className="flex flex-col items-center gap-3">
            <div className="h-24 w-24 overflow-hidden rounded-full border-2 border-[#D4AF37]/50 bg-[#D4AF37]/15 shadow-lg shadow-black/30 sm:h-28 sm:w-28">
              {profileImage ? (
                <img src={profileImage} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#D4AF37] to-[#8a6a14] font-display text-4xl font-black text-black">
                  {initial}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={imageBusy}
                className="inline-flex max-w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-bold text-black hover:bg-[#F1C40F] disabled:opacity-60"
              >
                <Camera className="h-4 w-4" /> {imageBusy ? "Loading..." : "Edit profile picture"}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
          </div>

          <div className="mt-5 space-y-4">
            <label className="block min-w-0">
              <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
                <User className="h-3.5 w-3.5" /> Display name
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                minLength={2}
                maxLength={40}
                required
                className="w-full min-w-0 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white outline-none transition-colors focus:border-[#D4AF37]"
              />
            </label>

          <label className="block min-w-0">
            <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
              <User className="h-3.5 w-3.5" /> Full name
            </span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              maxLength={80}
              className="w-full min-w-0 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white outline-none transition-colors focus:border-[#D4AF37]"
            />
          </label>

          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div className="block min-w-0 sm:col-span-2">
              <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
                <CalendarDays className="h-3.5 w-3.5" /> Date of birth
              </span>
              {user.date_of_birth ? (
                <>
                  <p className="rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white/70">{user.date_of_birth}</p>
                  <p className="mt-2 text-xs text-white/55">Birthday saved. Contact support if it needs correcting.</p>
                </>
              ) : (
                <>
                  <BirthDateFields value={dateOfBirth} onChange={setDateOfBirth} />
                  <p className="mt-2 text-xs text-white/55">Check your birthday before saving. It cannot be changed here after saving.</p>
                </>
              )}
            </div>

            <label className="block min-w-0">
              <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
                <MapPin className="h-3.5 w-3.5" /> State of USA
              </span>
              <select
                value={usState}
                onChange={(e) => setUsState(e.target.value)}
                className="w-full min-w-0 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white outline-none transition-colors focus:border-[#D4AF37]"
              >
                <option value="">Select</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>{state.name}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block min-w-0">
            <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
              <Mail className="h-3.5 w-3.5" /> Gmail / email
            </span>
            <input
              type="email"
              value={email}
              readOnly
              required
              className="w-full min-w-0 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white/55 outline-none"
            />
          </label>

          <label className="block min-w-0">
            <span className="mb-2 flex min-w-0 items-center gap-2 truncate text-xs uppercase tracking-[0.18em] text-white/45">
              <Phone className="h-3.5 w-3.5" /> Phone number
            </span>
            <input
              type="text"
              value={displayPhone}
              readOnly
              className="w-full min-w-0 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-white/55 outline-none"
            />
          </label>
          </div>
        </div>

        {saveError && <p role="alert" className="shrink-0 px-4 py-2 text-sm text-red-300 sm:px-6">{saveError}</p>}
        <div className="flex min-w-0 shrink-0 gap-2 border-t border-white/10 bg-[#07110B] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:gap-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="min-w-0 flex-1 rounded-full border border-white/10 px-3 py-3 text-sm font-bold text-white/70 hover:bg-white/10 hover:text-white sm:px-5 sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-3 py-3 text-sm font-bold text-black hover:bg-[#F1C40F] disabled:opacity-60 sm:px-5 sm:text-base"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(dialog, document.body);
}

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["FL", "Florida"], ["GA", "Georgia"],
  ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["DC", "District of Columbia"],
].map(([code, name]) => ({ code, name }));

function formatProfilePhone(value) {
  if (!value) return "Not verified yet";
  const digits = String(value).replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 10) {
    return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return String(value);
}

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const maxSide = 512;
        const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.86));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
